import {
    Description,
    FilterAltOffOutlined,
    FilterAltOutlined,
    LabelOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    ChipProps,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { GET } from "../../Utilities/BaseService";
import { S3Service } from "../../Utilities/S3Service";
import {
    awsDirStruct,
    filterSelectionMessage,
    noDataMessage,
} from "../../Utilities/constants";
import BaseTable from "../Table/BaseTable";
import { LogFilter } from "./LogFilter";
import { ActionResponse } from "./SingleLog";
import { SingleLogDetails } from "./SingleLogDetails";

/* ===================== UTILS ===================== */

const getDatesInRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];
    let curr = new Date(start);

    while (curr <= end) {
        dates.push(new Date(curr));
        curr.setDate(curr.getDate() + 1);
    }
    return dates;
};

const fetchLogsFromS3 = async (
    startDate: string,
    endDate: string,
    operator: any
) => {
    const dates = getDatesInRange(startDate, endDate);

    // Create promises for all dates in range
    const fetchPromises = dates.map(async (d) => {
        // MATCHES BACKEND: log_YYYY_M_D.log (Note: getMonth is 0-indexed)
        const key = `${awsDirStruct.logs}/log_${d.getFullYear()}_${d.getMonth()}_${d.getDate()}.log`;
        const [data, err] = await S3Service.getS3ByKey(key);

        if (data) {
            try {
                const response = await fetch(data.url);
                if (response.status !== 200) return [];
                const blob = await response.blob();
                const text = await blob.text();
                return JSON.parse(text);
            } catch (err) {
                console.error("Error fetching/parsing S3 file:", key, err);
                return [];
            }
        }
        return [];
    });

    const results = await Promise.all(fetchPromises);
    let allRows = results.flat();

    if (operator && operator !== "all") {
        allRows = allRows.filter((row: any) => row.operator.id == operator);
    }

    return {
        rows: allRows,
        count: allRows.length,
    };
};

const fetchLogs = async (
    filter: any = {},
    page = 0,
    limit = 100
): Promise<any> => {
    const todayStr = new Date().toISOString().split("T")[0];

    // 1. Determine ranges
    const includeToday =
        todayStr >= filter.startDate && todayStr <= filter.endDate;

    // Historical S3 fetching (Up to yesterday)
    let s3EndDate = filter.endDate;
    if (includeToday) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        s3EndDate = yesterday.toISOString().split("T")[0];
    }

    let s3Data = { rows: [], count: 0 } as any;
    if (s3EndDate >= filter.startDate) {
        s3Data = await fetchLogsFromS3(
            filter.startDate,
            s3EndDate,
            filter.operator
        );
    }

    // 2. Fetch live data if range includes today
    let dbDataRows = [];
    if (includeToday) {
        const res = await GET("/logs", {
            operator: filter.operator,
            page: 1,
            limit: 10000,
        });
        if (res.data) dbDataRows = res.data.rows;
    }

    // 3. Combine and Sort (Newest first)
    const combined = [...dbDataRows, ...s3Data.rows].sort(
        (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return {
        rows: combined,
        count: combined.length,
    };
};

/* ===================== ACTION TAGS ===================== */

type ActionTag = {
    label: string;
    color: ChipProps["color"];
    keywords: string[];
};

const ACTION_TAGS: ActionTag[] = [
    {
        label: "Reading",
        color: "primary",
        keywords: ["reading"],
    },
    {
        label: "Collection",
        color: "success",
        keywords: ["collected", "collection"],
    },
    {
        label: "Customer",
        color: "success",
        keywords: ["customer",],
    },
    {
        label: "Meter",
         color: "success",
        keywords: ["created a new connection", "updated a meter","replaced a meter", "deleted a meter"],
    },
    {
        label: "Auth",
         color: "success",
        keywords: ["login", "logout", "auth"],
    },
    {
        label: "Role",
         color: "success",
        keywords: ["role", "permission"],
    },
    {
        label: "Region",
         color: "success",
        keywords: ["district", "village", "hamlet", "powerstation"],
    },
    {
        label: "Payment",
         color: "success",
        keywords: ["payment", "advance"],
    },
    {
        label: "Advance",
         color: "success",
        keywords: ["advance"],
    },
];

const matchesTag = (actionText: string, tag: ActionTag): boolean => {
    const lower = actionText.toLowerCase();
    return tag.keywords.some((kw) => lower.includes(kw));
};

/* ===================== COMPONENT ===================== */

type Filter = {
    startDate: string;
    endDate: string;
    operator: "" | "all" | number;
};

const initialFilter: Filter = {
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    operator: "",
};

export const Logs = () => {
    const SESSION_TIME_IN_MINUTES = 120;
    const SESSION_GAP_SECONDS = SESSION_TIME_IN_MINUTES * 60;

    const [intermediateFilter, setIntermediateFilter] = useState(initialFilter);
    const [filter, setFilter] = useState(initialFilter);
    const [clearAll, setClearAll] = useState({
        operator: false,
        search: false,
    });
    const [changeCount, setChangeCount] = useState(0);
    const [pagination, setPagination] = useState({
        page: 0,
        count: 0,
        limit: 100,
    });
    const [logs, setLogs] = useState<any[]>([]);
    const [activeLog, setActiveLog] = useState<null | number>(null);
    const [feedback, setFeedback] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const timeFormatter = Intl.DateTimeFormat("en", {
        dateStyle: "short",
        timeStyle: "short",
    });

    useQuery(
        ["logs", filter, changeCount],
        () => fetchLogs(filter, pagination.page, pagination.limit),
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                setLogs(res.rows);
                setPagination((prev) => ({ ...prev, count: res.count }));
                setIsSearching(false);
            },
            onError() {
                setIsSearching(false);
            },
        }
    );

    const diffInSeconds = (from: string, to: string): number => {
        const start = new Date(from).getTime();
        const end = new Date(to).getTime();
        return isNaN(start) || isNaN(end)
            ? 0
            : Math.max(0, Math.floor((end - start) / 1000));
    };

    const calculateTotalWorkingTime = (logs: any[]): number => {
        if (!logs || logs.length < 2) return 0;
        const sortedLogs = [...logs].sort(
            (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
        );
        let totalSeconds = 0;
        for (let i = 0; i < sortedLogs.length - 1; i++) {
            const gap = diffInSeconds(
                sortedLogs[i].createdAt,
                sortedLogs[i + 1].createdAt
            );
            if (gap > 0 && gap <= SESSION_GAP_SECONDS) totalSeconds += gap;
        }
        return totalSeconds;
    };

    const formatHoursAndMinutes = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours === 0 ? `${minutes} min` : `${hours}h ${minutes}m`;
    };

    const parseActionText = (text: string, payload: any) => {
        try {
            const regex = /#[a-zA-Z]+/g;
            const matches = Array.from(text.matchAll(regex)).map(
                (exp) => exp[0]
            );
            const parsedPayload =
                typeof payload === "string" ? JSON.parse(payload) : payload;
            for (const match of matches) {
                text = text.replaceAll(
                    match,
                    parsedPayload[match.slice(1)] || match
                );
            }
        } catch (e) {}
        return text;
    };

    const handleClearAll = () => {
        setIntermediateFilter(initialFilter);
        setFilter(initialFilter);
        setClearAll({ operator: true, search: true });
        setChangeCount(0);
        setTimeout(() => setClearAll({ operator: false, search: false }), 0);
    };

    useEffect(() => {
        if (changeCount) setFilter({ ...intermediateFilter });
    }, [changeCount]);

    // ── Tag filtering (client-side, applied after fetch) ────────────────
    const filteredLogs =
        selectedTags.length === 0
            ? logs
            : logs.filter((log) =>
                  selectedTags.some((tagLabel) => {
                      const tag = ACTION_TAGS.find((t) => t.label === tagLabel);
                      return tag ? matchesTag(log.actionText ?? "", tag) : false;
                  })
              );

    const handleTagToggle = (tagLabel: string) => {
        setSelectedTags((prev) =>
            prev.includes(tagLabel)
                ? prev.filter((t) => t !== tagLabel)
                : [...prev, tagLabel]
        );
    };

    const totalWorkingTimeText = formatHoursAndMinutes(
        calculateTotalWorkingTime(filteredLogs)
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: mdDown ? "auto" : `calc(100vh - 96px)`,
            }}
        >
            <Card
                elevation={0}
                sx={{ bgcolor: theme.palette.common.white, p: 2, mb: "1rem", flexShrink: 0 }}
            >
                <Typography
                    variant="h5"
                    sx={{ p: "4px", mb: "4px" }}
                    color="GrayText"
                >
                    Logs
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                        <LogFilter
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            clearAll={clearAll}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            fullWidth
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOutlined />}
                            onClick={() => {
                                setIsSearching(true);
                                setChangeCount(changeCount + 1);
                            }}
                        >
                            {" "}
                            Search{" "}
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            color="error"
                            variant="outlined"
                            fullWidth
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                        >
                            {" "}
                            Clear All{" "}
                        </Button>
                    </Grid>

                    {/* ── Action Tag Filter Row ── */}
                    {logs.length > 0 && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    pt: 1,
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                    mt: 0.5,
                                }}
                            >
                                <LabelOutlined
                                    fontSize="small"
                                    sx={{ color: "text.secondary", flexShrink: 0 }}
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ flexShrink: 0 }}
                                >
                                    Filter by action:
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    <Tooltip title="Show all logs" placement="top">
                                        <Chip
                                            label="All"
                                            size="small"
                                            variant={selectedTags.length === 0 ? "filled" : "outlined"}
                                            color={selectedTags.length === 0 ? "primary" : "default"}
                                            onClick={() => setSelectedTags([])}
                                            sx={{ cursor: "pointer", fontWeight: selectedTags.length === 0 ? 700 : 400 }}
                                        />
                                    </Tooltip>
                                    {ACTION_TAGS.map((tag) => {
                                        const isActive = selectedTags.includes(tag.label);
                                        const matchCount = logs.filter((log) =>
                                            matchesTag(log.actionText ?? "", tag)
                                        ).length;
                                        if (matchCount === 0) return null;
                                        return (
                                            <Tooltip
                                                key={tag.label}
                                                title={`${matchCount} log${matchCount !== 1 ? "s" : ""}`}
                                                placement="top"
                                            >
                                                <Chip
                                                    label={`${tag.label} (${matchCount})`}
                                                    size="small"
                                                    variant={isActive ? "filled" : "outlined"}
                                                    color={isActive ? tag.color : "default"}
                                                    onClick={() => handleTagToggle(tag.label)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        fontWeight: isActive ? 700 : 400,
                                                        transition: "all 0.15s ease",
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Card>

            {isSearching ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50%"
                >
                    <CircularProgress />
                </Box>
            ) : logs.length > 0 ? (
                <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Total Working Time - {totalWorkingTimeText}
                        {selectedTags.length > 0 && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                            >
                                (showing {filteredLogs.length} of {logs.length} logs)
                            </Typography>
                        )}
                    </Typography>
                    <BaseTable
                        delEndPoint=""
                        headers={{
                            createdAt: "Timestamp",
                            operator: "Operator",
                            action: "Action",
                            details: "View Details",
                            status: "Status",
                        }}
                        data={filteredLogs.map((log) => ({
                            createdAt: timeFormatter.format(
                                new Date(log.createdAt)
                            ),
                            operator: log.operator?.name || "N/A",
                            action: parseActionText(
                                log.actionText,
                                log.payload
                            ),
                            details: (
                                <>
                                    <IconButton
                                        size="small"
                                        onClick={() => setActiveLog(log.id)}
                                    >
                                        <Description fontSize="small" />
                                    </IconButton>
                                    <SingleLogDetails
                                        open={activeLog === log.id}
                                        setOpen={setActiveLog}
                                        log={log}
                                    />
                                </>
                            ),
                            status: (
                                <ResponseIndicator response={log.response} />
                            ),
                        }))}
                        pagination={{
                            page: pagination.page,
                            limit: pagination.limit,
                        }}
                        setPagination={setPagination}
                        rowsCount={pagination.count}
                        isLoading={isSearching}
                        permissions={{ edit: [""], delete: [""] }}
                        reportName={"Logs"}
                        load={Boolean(changeCount)}
                        queryKey={[]}
                        defaultSelectedHeaders={[
                            "createdAt",
                            "operator",
                            "action",
                            "details",
                            "status",
                        ]}
                        showPagination={false}
                    />
                </Box>
            ) : (
                <Alert severity="info">
                    {filter.operator ? noDataMessage : filterSelectionMessage}
                </Alert>
            )}
        </Box>
    );
};

const ResponseIndicator = ({ response }: { response: ActionResponse }) => {
    const chipDefaults: ChipProps = { size: "small" };
    if (response === ActionResponse.Success)
        return <Chip color="success" label="Success" {...chipDefaults} />;
    if (response === ActionResponse.Failure)
        return <Chip color="error" label="Failed" {...chipDefaults} />;
    return <Chip label="Unknown" {...chipDefaults} />;
};
